import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useState } from 'react';

import {
  useCreateLaunchWaiver,
  useDeleteLaunchWaiver,
  useLaunchReadiness,
} from '../hooks/use-content';
import { useT, type MessageKey } from '../i18n';
import type { LaunchWaiver, ReadinessCheck, ReadinessCheckState } from '../lib/api';
import { errorMessage } from '../lib/ui';

/**
 * PRE-289 — are we fit to put this in front of a stranger.
 *
 * Two screens already answer neighbouring questions and neither is this one.
 * Bank health asks "is the bank broken"; question flow asks "where is the
 * pipeline stuck". Neither says whether we can ship.
 *
 * The shape here is a data-quality scorecard with a gate on top, which is what
 * the practice actually is: a target per dimension, one owner, and a launch that
 * blocks below threshold. The one part a person writes is a waiver — a written,
 * time-boxed exception with a named owner — because "we are shipping with this
 * gap open" is a decision, not a measurement.
 *
 * NOTHING IN THE WORKLIST IS TICKABLE, and that is the point rather than an
 * omission. Ticking does not change a fact: the moment a fifth question lands on
 * a cell its row closes, and if an item is retired tomorrow the row comes back. A
 * hand-maintained list cannot do that, and from the first day it goes stale it is
 * lying to whoever reads it. So there is no checkbox to offer.
 */

const STATE_COLOR: Record<ReadinessCheckState, string> = {
  pass: 'green',
  blocked: 'red',
  waived: 'yellow',
  info: 'blue',
};

const STATE_KEY: Record<ReadinessCheckState, MessageKey> = {
  pass: 'launch.state.pass',
  blocked: 'launch.state.blocked',
  waived: 'launch.state.waived',
  info: 'launch.state.info',
};

/**
 * The waiver box.
 *
 * Every field is required, and the form says why rather than just refusing:
 * a reason nobody wrote is indistinguishable six months later from a gap nobody
 * noticed, a waiver nobody's name is on is one nobody agreed to, and one with no
 * expiry is not an exception but a quiet lowering of the bar.
 */
function WaiverModal({
  check,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  check: ReadinessCheck | null;
  onClose: () => void;
  onSubmit: (input: { reason: string; owner: string; expiresAt: string }) => void;
  pending: boolean;
  error: string | null;
}) {
  const t = useT();
  const [reason, setReason] = useState('');
  const [owner, setOwner] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const close = () => {
    setReason('');
    setOwner('');
    setExpiresAt('');
    onClose();
  };

  const today = new Date().toISOString().slice(0, 10);
  const valid = reason.trim().length >= 10 && owner.trim().length > 0 && expiresAt > today;

  return (
    <Modal
      opened={check != null}
      onClose={close}
      title={t('launch.waiverModalTitle', { title: check?.title ?? '' })}
      centered
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t('launch.waiverIntro')}
        </Text>
        <Textarea
          label={t('launch.reason')}
          description={t('launch.reasonHint')}
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <TextInput
          label={t('launch.owner')}
          description={t('launch.ownerHint')}
          value={owner}
          onChange={(e) => setOwner(e.currentTarget.value)}
        />
        <TextInput
          label={t('launch.expiresAt')}
          description={t('launch.expiresAtHint')}
          placeholder="2026-12-31"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.currentTarget.value)}
        />
        {error ? <Alert color="red">{error}</Alert> : null}
        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            color="yellow"
            loading={pending}
            disabled={!valid}
            onClick={() => onSubmit({ reason: reason.trim(), owner: owner.trim(), expiresAt })}
          >
            {t('launch.submitWaiver')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function LaunchReadinessScreen() {
  const t = useT();
  const { data, isLoading, error } = useLaunchReadiness();
  const createWaiver = useCreateLaunchWaiver();
  const deleteWaiver = useDeleteLaunchWaiver();
  const [waiving, setWaiving] = useState<ReadinessCheck | null>(null);

  if (isLoading) return <Loader />;
  if (error) return <Alert color="red">{errorMessage(error)}</Alert>;
  if (!data) return null;

  const blocking = data.checks.filter((c) => !c.advisory && c.state === 'blocked');

  return (
    <Stack gap="lg">
      <Title order={2}>{t('launch.title')}</Title>

      {/* The gate, and only the gate. A reader who looks at nothing else should
          leave with the right answer. */}
      <Alert color={data.ready ? 'green' : 'red'}>
        <Text fw={700}>
          {data.ready
            ? t('launch.noBlockers')
            : t('launch.blockers', {
                count: data.blockingCount,
                names: blocking.map((c) => c.title).join(', '),
              })}
        </Text>
        <Text size="sm" mt={4}>
          {t(data.ready ? 'launch.readyNote' : 'launch.notReadyNote')}
        </Text>
      </Alert>

      {data.expiredWaivers.length > 0 ? (
        <Alert color="orange" title={t('launch.expiredTitle')}>
          {t('launch.expiredBody')}
          <Stack gap={2} mt="xs">
            {data.expiredWaivers.map((w: LaunchWaiver) => (
              <Text key={w.id} size="sm">
                {t('launch.expiredLine', {
                  key: w.checkKey,
                  owner: w.owner,
                  date: w.expiresAt,
                })}
              </Text>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <div>
        <Title order={4}>{t('launch.scorecard')}</Title>
        <Text size="sm" c="dimmed" mb="xs">
          {t('launch.scorecardHint')}
        </Text>
        <Stack gap="xs">
          {data.checks.map((check) => (
            <Card key={check.key} withBorder padding="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1 }}>
                  <Group gap="xs">
                    <Text fw={600}>{check.title}</Text>
                    <Badge color={STATE_COLOR[check.state]}>{t(STATE_KEY[check.state])}</Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {check.question}
                  </Text>
                  {check.waiver ? (
                    <Text size="sm" mt={4}>
                      {t('launch.waiverLine', {
                        reason: check.waiver.reason,
                        owner: check.waiver.owner,
                        date: check.waiver.expiresAt,
                      })}
                    </Text>
                  ) : null}
                </div>
                <Stack gap={4} align="flex-end" w={180}>
                  <Text size="sm" fw={600}>
                    {check.actual} / {check.target}
                  </Text>
                  <Progress
                    w={160}
                    value={check.target === 0 ? 0 : (check.actual / check.target) * 100}
                    color={STATE_COLOR[check.state]}
                  />
                  {!check.advisory && check.state === 'blocked' ? (
                    <Button size="compact-xs" variant="light" onClick={() => setWaiving(check)}>
                      {t('launch.writeWaiver')}
                    </Button>
                  ) : null}
                </Stack>
              </Group>
            </Card>
          ))}
        </Stack>
      </div>

      <div>
        <Title order={4}>{t('launch.worklist')}</Title>
        <Text size="sm" c="dimmed" mb="xs">
          {t('launch.worklistHint')}
        </Text>
        {data.worklist.length === 0 ? (
          <Alert color="green">{t('launch.noWork')}</Alert>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('launch.colTodo')}</Table.Th>
                <Table.Th w={120}>{t('launch.colDimension')}</Table.Th>
                <Table.Th w={90}>{t('launch.colDeficit')}</Table.Th>
                <Table.Th w={140}>{t('launch.colScreen')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.worklist.map((item, i) => (
                <Table.Tr key={`${item.checkKey}-${item.label}-${i}`}>
                  <Table.Td>{item.label}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {data.checks.find((c) => c.key === item.checkKey)?.title ?? item.checkKey}
                    </Text>
                  </Table.Td>
                  <Table.Td>{item.deficit}</Table.Td>
                  <Table.Td>
                    {item.screen ? (
                      <Button size="compact-xs" variant="subtle" component="a" href={item.screen}>
                        {item.screen}
                      </Button>
                    ) : (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </div>

      {data.checks.some((c) => c.state === 'waived') ? (
        <div>
          <Title order={4}>{t('launch.openWaivers')}</Title>
          <Text size="sm" c="dimmed" mb="xs">
            {t('launch.openWaiversHint')}
          </Text>
          <Stack gap="xs">
            {data.checks
              .filter((c) => c.waiver)
              .map((c) => (
                <Card key={c.key} withBorder padding="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <div>
                      <Text fw={600}>{c.title}</Text>
                      <Text size="sm">{c.waiver!.reason}</Text>
                      <Text size="xs" c="dimmed">
                        {t('launch.until', {
                          owner: c.waiver!.owner,
                          date: c.waiver!.expiresAt,
                        })}
                      </Text>
                    </div>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      loading={deleteWaiver.isPending}
                      onClick={() => deleteWaiver.mutate(c.waiver!.id)}
                    >
                      {t('launch.revoke')}
                    </Button>
                  </Group>
                </Card>
              ))}
          </Stack>
        </div>
      ) : null}

      <WaiverModal
        check={waiving}
        pending={createWaiver.isPending}
        error={createWaiver.error ? errorMessage(createWaiver.error) : null}
        onClose={() => {
          setWaiving(null);
          createWaiver.reset();
        }}
        onSubmit={(input) => {
          if (!waiving) return;
          createWaiver.mutate(
            { checkKey: waiving.key, ...input },
            { onSuccess: () => setWaiving(null) },
          );
        }}
      />
    </Stack>
  );
}
